import { Box, Card, Table, Text } from "@mantine/core";
import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import type { ReactNode, RefObject } from "react";

/**
 * The desktop grid: one table for the whole app.
 *
 * WHY THIS EXISTS
 *
 * Every list in this app was a stack of cards at every width, including
 * 1440px, where a card per row wastes most of the screen and puts the two
 * things you scan for - what state is it in, and what is the next step - on
 * different lines of different cards. That is observation 5.
 *
 * Four screens needed the same table and none of them had one. Writing it
 * four times would have given four keyboard behaviours, four ideas of what a
 * selected row looks like, and four places for the column widths to drift,
 * which is the argument that already produced components/ui.
 *
 * WHAT IT IS AND IS NOT
 *
 * A real <table>. Not a grid of divs: the semantics are what lets a screen
 * reader say "row 4 of 23, column Stage", and no amount of aria puts that
 * back once you have thrown the element away.
 *
 * It is NOT a data grid. No sorting, no column resizing, no virtualisation,
 * no inline editing. Every list it serves is server-filtered and under a few
 * hundred rows, and an editable cell is the thing this app most deliberately
 * does not have - a stock count is a delta with a reason, a price is a dated
 * row in a history.
 *
 * KEYBOARD
 *
 * j/k move the cursor, Enter opens, and the cursor is a real one: it draws,
 * it scrolls into view, and aria-activedescendant points at it so assistive
 * technology follows. Arrow keys do the same thing as j/k, because somebody
 * who has never used vi should not be locked out of the feature.
 *
 * `/` focuses the search box. That lives in useSlashToFocus rather than here,
 * because the search field belongs to the screen, and a table that reached
 * out and grabbed a sibling's input would only work for screens that put it
 * where this one expected.
 */

export interface Column<T> {
  /** Stable identity, used for the React key and the CSV header. */
  key: string;
  header: ReactNode;
  /**
   * A CSS width for the <col>. The brief gives these per screen.
   *
   * A LENGTH or "auto" - never an `fr`. `fr` is a grid unit; on a <col> it is
   * invalid, the browser drops it, and under table-layout:fixed that column
   * ends up with no width at all. Two headers printed on top of each other
   * before this was understood. Exactly one column should be "auto": it takes
   * whatever the fixed ones leave.
   */
  width?: string;
  align?: "left" | "right";
  render: (row: T) => ReactNode;
}

/*
 * A NOTE ON WHERE THE EXPORT LIVES.
 *
 * A `value?: (row) => string` used to sit on this interface, next to
 * `render`, and it was what the CSV export wrote. It is gone, along with
 * `toCsv`, `downloadCsv` and the fifty-five `value` functions the screens
 * each supplied.
 *
 * The export is now the server's - see src/services/export.service.ts - for a
 * reason no amount of browser code could fix: every list endpoint caps its
 * rows, so an export written from what the screen holds is an export of at
 * most 200 records from a list that may have thousands, in a file that gives
 * no sign of it.
 *
 * `value` was deleted rather than left in place because a column function
 * that nothing reads is worse than no function at all: the next person to
 * change what an export contains would have edited it here, in the obvious
 * place, and watched the file not change.
 */

export function DataTable<T>({
  rows,
  columns,
  rowKey,
  onOpen,
  activeKey,
  rowMuted,
  caption,
  emptyLabel = "Nothing to show",
}: {
  rows: readonly T[];
  columns: readonly Column<T>[];
  rowKey: (row: T) => string;
  /** Enter, or a click. Omit for a table that is only read. */
  onOpen?: (row: T) => void;
  /**
   * A row to tint, decided by the SCREEN rather than by the cursor - the
   * product being edited in the drawer, say. Separate from the cursor on
   * purpose: they answer different questions ("what am I working on" versus
   * "where is the keyboard").
   */
  activeKey?: string;
  /**
   * A row that is no longer live - a discontinued product, say.
   *
   * Applied to the <tr>, so it is ONE decision per row rather than the same
   * conditional repeated in every cell. It was per-cell first, and the six
   * copies had already drifted: the row's own colour came out as ordinary
   * ink, which a drive check caught.
   *
   * Never the only signal. A muted row still carries its chip - "a row that
   * is merely fainter is one people read as normal" - this just stops the
   * text competing with the live rows around it.
   */
  rowMuted?: (row: T) => boolean;
  /** Read out before the table. Not drawn - the page header already says it. */
  caption?: string;
  emptyLabel?: string;
}) {
  const [cursor, setCursor] = useState(0);
  const bodyRef = useRef<HTMLTableSectionElement>(null);
  // React's own, not a random string: it is stable across renders and stable
  // between server and client, which a Math.random() id is not.
  const tableId = useId();

  // A shrinking list must not leave the cursor past the end - and a cursor
  // that silently pointed at nothing would make Enter do nothing with no
  // way to tell why.
  useEffect(() => {
    setCursor((current) => (current >= rows.length ? Math.max(rows.length - 1, 0) : current));
  }, [rows.length]);

  const move = useCallback(
    (delta: number) => {
      setCursor((current) => {
        const next = Math.min(Math.max(current + delta, 0), Math.max(rows.length - 1, 0));
        // Scrolling is the difference between a cursor and a cursor you can
        // find. block:"nearest" so it only scrolls when it has to.
        const node = bodyRef.current?.children[next] as HTMLElement | undefined;
        node?.scrollIntoView({ block: "nearest" });
        return next;
      });
    },
    [rows.length],
  );

  function onKeyDown(event: React.KeyboardEvent<HTMLTableElement>) {
    if (event.key === "j" || event.key === "ArrowDown") {
      event.preventDefault();
      move(1);
    } else if (event.key === "k" || event.key === "ArrowUp") {
      event.preventDefault();
      move(-1);
    } else if (event.key === "Enter" && onOpen && rows[cursor]) {
      event.preventDefault();
      onOpen(rows[cursor]!);
    } else if (event.key === "Home") {
      event.preventDefault();
      move(-rows.length);
    } else if (event.key === "End") {
      event.preventDefault();
      move(rows.length);
    }
  }

  const cursorRowId = rows[cursor] ? `${tableId}-${rowKey(rows[cursor]!)}` : undefined;

  return (
    <Card withBorder padding={0}>
      <Table
        layout="fixed"
        highlightOnHover={onOpen !== undefined}
        verticalSpacing={8}
        horizontalSpacing={12}
        tabIndex={0}
        onKeyDown={onKeyDown}
        aria-activedescendant={cursorRowId}
        style={{ outlineOffset: -2 }}
      >
        {caption && <Table.Caption className="visually-hidden">{caption}</Table.Caption>}
        <colgroup>
          {columns.map((column) => (
            <col key={column.key} style={column.width ? { width: column.width } : undefined} />
          ))}
        </colgroup>
        <Table.Thead>
          <Table.Tr>
            {columns.map((column) => (
              <Table.Th key={column.key} ta={column.align ?? "left"}>
                <Text
                  fz={12}
                  fw={700}
                  c="gray.6"
                  tt="uppercase"
                  style={{ letterSpacing: ".04em" }}
                >
                  {column.header}
                </Text>
              </Table.Th>
            ))}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody ref={bodyRef}>
          {rows.map((row, index) => {
            const key = rowKey(row);
            const isCursor = index === cursor;
            const isActive = activeKey !== undefined && activeKey === key;
            return (
              <Table.Tr
                key={key}
                id={`${tableId}-${key}`}
                onClick={
                  onOpen
                    ? (event: React.MouseEvent<HTMLTableRowElement>) => {
                        // A click on a CONTROL inside the row is that
                        // control's, not the row's. Without this, ticking a
                        // checkbox also opened the record - the tick never
                        // registered because the screen navigated away.
                        //
                        // Handled here rather than by every cell remembering
                        // to stopPropagation: one of them will forget, and
                        // the symptom is a control that silently does the
                        // wrong thing rather than an error anybody sees.
                        const target = event.target as HTMLElement;
                        if (target.closest("input, button, a, label, select, textarea")) {
                          return;
                        }
                        setCursor(index);
                        onOpen(row);
                      }
                    : undefined
                }
                style={{
                  cursor: onOpen ? "pointer" : undefined,
                  // The screen's choice wins over the keyboard's: if you are
                  // editing row 3 and the cursor is on row 7, the one being
                  // edited is the one that must stay obvious.
                  background: isActive ? "#edf2ff" : undefined,
                  color: rowMuted?.(row) ? "#868e96" : undefined,
                  // The cursor is a left bar rather than a fill, so it can be
                  // seen ON a tinted row instead of being swallowed by it.
                  boxShadow: isCursor ? "inset 3px 0 0 0 #3b5bdb" : undefined,
                }}
              >
                {columns.map((column) => (
                  <Table.Td key={column.key} ta={column.align ?? "left"}>
                    {column.render(row)}
                  </Table.Td>
                ))}
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>
      {rows.length === 0 && (
        <Box p="md">
          <Text fz="sm" c="gray.6">
            {emptyLabel}
          </Text>
        </Box>
      )}
    </Card>
  );
}

/**
 * `/` focuses the screen's search box.
 *
 * Bound to the document, because the point of the shortcut is that it works
 * wherever you are on the screen. It stands down while you are typing - in an
 * input, a textarea, a select or anything contenteditable - or a slash typed
 * into the search box itself would bounce the cursor back to the start of it.
 */
export function useSlashToFocus(ref: RefObject<HTMLInputElement | null>): void {
  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key !== "/" || event.metaKey || event.ctrlKey || event.altKey) return;
      const target = event.target as HTMLElement | null;
      const tag = target?.tagName;
      if (
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        tag === "SELECT" ||
        target?.isContentEditable
      ) {
        return;
      }
      event.preventDefault();
      ref.current?.focus();
      ref.current?.select();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [ref]);
}

/** Memo helper: columns are defined inline and must not re-create every render. */
export function useColumns<T>(
  factory: () => Column<T>[],
  deps: readonly unknown[],
): Column<T>[] {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(factory, deps);
}
